import { useState } from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxHubspotform } from '@/components/01-atoms/Hubspotform/Hubspotform'
import { GoogleMap } from '@/components/maps/GoogleMap'

import HoursIcon from '../../../../public/svg/hours.svg'
import OfficeIcon from '../../../../public/svg/location.svg'
import PhoneIcon from '../../../../public/svg/phone.svg'
import { GoogleMapImage } from '../../maps/GoogleMapImage'
import styles from './OfficeDetails.module.css'

export interface OfficeDetailsProps {
  variant?: 'default' | 'card'
  title?: string
  link?: string
  address: string
  phone: string
  hours?: string
  formId?: string
  form?: Array<{
    component: string
    fields?: Array<{
      component: string
      _uid: string
      variant:
        | 'number'
        | 'hidden'
        | 'textarea'
        | 'text'
        | 'checkbox'
        | 'radio'
        | 'email'
        | 'subscribe'
        | 'select'
        | 'phone'
        | 'multi'
        | 'enquiry'
        | 'state'
        | 'valueLabel'
      name: string
      label: string
      placeholder?: string
      required?: boolean
      showRequired?: boolean
      radioOptions?: string[]
      customField_answer?: string
      hiddenValue?: string
      options?: Array<{ label: string; value: string }>
      multiOptions?: string[]
      phoneEnquiryPersonal?: string[]
      phoneEnquiryBusiness?: string[]
      fields?: Array<{
        component: string
        _uid: string
        variant:
          | 'number'
          | 'hidden'
          | 'textarea'
          | 'text'
          | 'checkbox'
          | 'radio'
          | 'email'
          | 'subscribe'
          | 'select'
          | 'phone'
          | 'multi'
          | 'enquiry'
          | 'state'
          | 'valueLabel'
        name: string
        label: string
        placeholder?: string
        required?: boolean
        showRequired?: boolean
        radioOptions?: string[]
        customField_answer?: string
        hiddenValue?: string
        options?: Array<{ label: string; value: string }>
        multiOptions?: string[]
        phoneEnquiryPersonal?: string[]
        phoneEnquiryBusiness?: string[]
      }>
    }>
    endpoint?: string
    title?: string
    cta_label?: string
    cta_full_mobile?: boolean
    cta_position?: 'center' | 'left' | 'right'
    cta_bold?: boolean
    form_color?: string
    textColour?: string
    disclaimer?: string
    subContent?: string
    lead_source?: string
    campaign_id?: string
    lead_type?: string
    submission_url?: string
    isSubscribeForm?: boolean
    variant?: string
    shadow?: boolean
  }>
  mailingAddress?: string
  location: { lat: number; long: number }
}

interface OfficeDetailsMainProps extends OfficeDetailsProps {
  onContactButtonClick: () => void
}

interface OfficeDetailsFormProps extends OfficeDetailsProps {
  onBackButtonClick: () => void
}

const OfficeDetailsForm = ({ onBackButtonClick, title }: OfficeDetailsFormProps) => {
  const keyDownHandler: React.HTMLAttributes<HTMLElement>['onKeyDown'] = (event) => {
    if (event.key === 'Enter') {
      onBackButtonClick()
    }
  }

  // Get current location from environment variable
  const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'default'
  const isNZLocale = location === 'en-nz'

  return (
    <div data-testid="details-form" className={styles['details-form']}>
      <button
        className={styles['form-heading']}
        data-testid="back-to-details"
        onClick={onBackButtonClick}
        onKeyDown={keyDownHandler}
        tabIndex={0}
        aria-label={`Back to details`}
      >
        <i className={`fx-icon fx-icon-arrow-down-lg ${styles['back-icon']}`}></i>
        Back to details
      </button>

      <PhnxHubspotform
        title="Back to details"
        formId={
          isNZLocale
            ? '1421bd71-d9da-4940-a97c-f70bc9270731'
            : '5041250a-4332-4114-bb1d-a5739827fbec'
        }
        hiddenFields={{
          '0-1/last_submitted_office_location': (title || '').replace(/^Findex\s*/, ''),
        }}
      />
    </div>
  )
}

const OfficeDetailsMain = ({
  onContactButtonClick,
  location,
  address,
  phone,
  hours,
  mailingAddress,
}: OfficeDetailsMainProps) => {
  return (
    <>
      <div className={styles['office-info']}>
        <div>
          {address && (
            <>
              <span className={styles['office-info__label']}>
                <OfficeIcon viewBox="0 0 26 36" className="inline mr-1 lg:mr-2.5" /> Office address:
              </span>
              <p>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.long}`}
                >
                  {address}
                </a>
              </p>
            </>
          )}

          {phone && (
            <>
              <span className={styles['office-info__label']}>
                <PhoneIcon viewBox="0 0 25 25" className="inline mr-2 lg:mr-5" />
                Phone:
              </span>
              <p>
                <a href={`tel:${phone}`}>{phone}</a>
              </p>
            </>
          )}

          {(hours ?? '').length > 0 && (
            <>
              <span className={styles['office-info__label']}>
                <HoursIcon viewBox="0 0 28 30" className="inline mr-1 lg:mr-2" /> Hours:
              </span>
              <p dangerouslySetInnerHTML={{ __html: hours ?? '' }} />
            </>
          )}

          {(mailingAddress ?? '').length > 0 && (
            <>
              <span className={styles['office-info__label']}>
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4 inline-block mr-1 -mt-[2px]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>{' '}
                Mailing address:
              </span>
              <p>{mailingAddress}</p>
            </>
          )}
        </div>

        <div className={styles['button-group']}>
          <PhnxButton
            size="large"
            type="link"
            label="Contact Findex"
            onClick={onContactButtonClick}
            data-testid="contact-btn"
          >
            Contact Findex
          </PhnxButton>

          <PhnxButton
            css={{ fontWeight: 600 }}
            variant="quaternary"
            size="large"
            type="link"
            href={`https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.long}`}
            label="Office directions"
            target="_blank" // Open in new tab
            data-testid="office-direction-btn"
          />
        </div>
      </div>

      <div className={(styles['map-wrapper'], 'map-wrapper')}>
        <GoogleMap lat={Number(location.lat)} lng={Number(location.long)} />
      </div>
    </>
  )
}

const OfficeDetailsCard = ({ title, location, address, phone, link }: OfficeDetailsProps) => {
  return (
    <div className={styles['office-card']}>
      <div className={styles.cardmapwrapper}>
        <GoogleMapImage
          lat={Number(location.lat)}
          lng={Number(location.long)}
          showMarker={false}
          defaultZoom={15}
        />
      </div>
      <div>
        {(title ?? '').length > 0 && <h4 className={styles.officecardTitle}>{title}</h4>}
        {address.length > 0 && (
          <PhnxButton type="link" href={link} label={address} size="default" variant="quinary" />
        )}
        {phone.length > 0 && (
          <p>
            Phone:{' '}
            <a href={`tel:${phone}`} className={styles.officecardAddress}>
              {phone}
            </a>
          </p>
        )}
      </div>
    </div>
  )
}

export const PhnxOfficeDetails: React.FC<OfficeDetailsProps> = ({
  variant = 'default',
  ...props
}) => {
  const [showForm, setShowForm] = useState(false)

  return (
    <>
      {variant === 'card' && <OfficeDetailsCard {...props} />}
      {variant !== 'card' && (
        <div className={styles['office-details']}>
          {variant === 'default' && !showForm && (
            <OfficeDetailsMain onContactButtonClick={() => setShowForm(true)} {...props} />
          )}
          {variant === 'default' && showForm && (
            <OfficeDetailsForm onBackButtonClick={() => setShowForm(false)} {...props} />
          )}
        </div>
      )}
    </>
  )
}
